import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import PendingEnrollmentModal from '../../components/PendingEnrollmentModal';
import { adminService, academicService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { Card, Badge, Select, Input, Spinner, EmptyState, Pagination } from '../../components/ui';
import { formatPrice } from '../../utils/currency';

const STATUS_OPTIONS = ['pending', 'active', 'expired', 'cancelled', 'rejected'];
const PAGE_SIZE = 20;

function AdminEnrollments() {
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [enrollments, setEnrollments] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [moduleFilter, setModuleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [detailEnrollment, setDetailEnrollment] = useState(null);

  useEffect(() => {
    academicService.getModules({ page_size: 200 })
      .then((res) => setModules(res.data.results || res.data || []))
      .catch(() => {});
  }, []);

  const fetchEnrollments = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    if (statusFilter) params.status = statusFilter;
    if (moduleFilter) params.module = moduleFilter;
    if (search.trim()) params.search = search.trim();
    adminService.getEnrollments(params)
      .then((res) => {
        const data = res.data;
        setEnrollments(Array.isArray(data) ? data : data.results || []);
        setCount(Array.isArray(data) ? data.length : data.count || 0);
      })
      .catch((e) => addNotification(getApiErrorMessage(e, t, t('Failed to load enrollments')), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, moduleFilter, search]);

  useEffect(() => {
    const debounce = setTimeout(fetchEnrollments, search ? 350 : 0);
    return () => clearTimeout(debounce);
  }, [fetchEnrollments, search]);

  useEffect(() => { setPage(1); }, [statusFilter, moduleFilter, search]);

  const onStatusChange = (value) => {
    setStatusFilter(value);
    setSearchParams(value ? { status: value } : {});
  };

  const handleDecided = () => {
    setDetailEnrollment(null);
    fetchEnrollments();
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Enrollments')} subtitle={t('All enrollment requests across every course')} />
        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="flex flex-wrap gap-3">
            <Select value={statusFilter} onChange={(e) => onStatusChange(e.target.value)} className="sm:max-w-xs">
              <option value="">{t('All statuses')}</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{t(s)}</option>)}
            </Select>
            <Select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} className="sm:max-w-xs">
              <option value="">{t('All courses')}</option>
              {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </Select>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('Search by student name or email')}
              className="sm:max-w-xs"
            />
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : enrollments.length === 0 ? (
            <Card><EmptyState title={t('No enrollments match these filters')} /></Card>
          ) : (
            <Card padded={false} className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">{t('Student')}</th>
                      <th className="text-left px-4 py-3">{t('Course')}</th>
                      <th className="text-left px-4 py-3">{t('Plan')}</th>
                      <th className="text-left px-4 py-3">{t('Price')}</th>
                      <th className="text-left px-4 py-3">{t('Status')}</th>
                      <th className="text-left px-4 py-3">{t('Requested')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {enrollments.map((e) => (
                      <tr
                        key={e.id}
                        onClick={() => e.status === 'pending' && setDetailEnrollment(e)}
                        className={`hover:bg-gray-50 ${e.status === 'pending' ? 'cursor-pointer' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{e.student?.first_name} {e.student?.last_name}</div>
                          <div className="text-xs text-gray-400">{e.student?.email}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{e.module_name}</td>
                        <td className="px-4 py-3 text-gray-600">{e.subscription_name}</td>
                        <td className="px-4 py-3 text-gray-900 font-medium">
                          {formatPrice(e.agreed_price ?? e.discounted_price ?? e.module_price, e.module_currency)}
                        </td>
                        <td className="px-4 py-3"><Badge status={e.status} /></td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{new Date(e.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} />
            </Card>
          )}
        </main>
      </div>

      <PendingEnrollmentModal
        enrollment={detailEnrollment}
        onClose={() => setDetailEnrollment(null)}
        onApproved={handleDecided}
        onRejected={handleDecided}
      />
    </div>
  );
}

export default AdminEnrollments;
